import type { IServiceEmploymentRight } from '@frontend/components/pages/personalSettings/carePathOverview/PersonalSettingsCarePathsOverview';
import type { IRight } from '../../common/interfaces/rights';
import { isEpisodeCategoryRight, isServiceRight, ServiceRightType } from '../../common/interfaces/rights';
import { never } from '../../common/utils/never';
import { translateKey } from '../translate';
import { getServiceProviderTitle } from './getServiceProviderTitle';

export function formatRight(
    right: Omit<IRight, 'serviceGroup'>,
    displaySettings: 'showServiceName' | 'showServiceType' = 'showServiceName'
) {
    if (isEpisodeCategoryRight(right)) {
        return `${translateKey('addCarePathSectionTitles.serviceReaderSettings')} ${translateKey(
            `episodeCategoryEnum.${right.episodeCategory.name}`
        ).toLowerCase()}`;
    }

    if (isServiceRight(right) && right.rightType !== ServiceRightType.serviceEmployment) {
        const servicePart =
            displaySettings === 'showServiceType'
                ? `${translateKey(`serviceTypeEnum.${right.service.serviceType}`).toLowerCase()}`
                : ` ${right.service.name.toLowerCase()}`;

        const rightType = right.rightType;
        switch (rightType) {
            case ServiceRightType.serviceConsumer:
                return `${translateKey('addCarePathSectionTitles.serviceConsumerSettings')} ${servicePart} ${
                    right.isRequestCreator ? '(direct)' : '(indirect)'
                }`;
            case ServiceRightType.serviceProvider:
                return `${translateKey(
                    'addCarePathSectionTitles.serviceProviderSettings'
                )} ${servicePart} ${getServiceProviderTitle(right)}`;
            case ServiceRightType.serviceSupervisor:
                return `${translateKey('addCarePathSectionTitles.serviceSupervisorSettings')} ${servicePart}`;
            default:
                never(rightType);
        }
    }

    throw new Error(`Unknown IRight for ${JSON.stringify(right)} `);
}

export function formatServiceEmploymentRight(right: IServiceEmploymentRight) {
    return right.serviceActivityAuthorizations
        .map(
            (authorization) =>
                `${authorization.serviceActivity.name} (${
                    authorization.canWrite
                        ? translateKey('addCarePathSettingsLabelsServiceEmployment.write')
                        : translateKey('addCarePathSettingsLabelsServiceEmployment.read')
                })`
        )
        .join(', ');
}
